import { css } from 'aphrodite';
import { FormEventHandler, useCallback, useMemo } from 'react';
import { Control, FormState } from 'react-hook-form';
import { Button } from '../../../../../web/components-v2/Button';
import { Separator } from '../../../../../web/components/Separator';
import { IAddress, IDonationSurvey } from '../../../../models/Api';
import { DonationFrequency } from '../../../../models/enums/DonationFrequency';
import { CustomerDataCollection } from '../CustomerDataCollection';
import { DonationSurveyHeader } from '../DonationSurveyHeader';
import { DonationSurveyValuePicker } from '../DonationSurveyValuePicker';
import { SpecificCauseSelector } from '../SpecificCauseSelector';
import { IFormValues } from '../utils';
import { CustomForm } from './CustomForm';
import { styleSheet } from './styles';

interface IProps {
	/** The donation survey being displayed */
	survey: IDonationSurvey;
	/** Current form values */
	formValues: IFormValues;
	/** Function to update form field values */
	setValue: (name: keyof IFormValues, value: string | number | boolean | IAddress) => void;
	/** Form submission handler */
	onSubmit: FormEventHandler<HTMLFormElement>;
	/** React Hook Form control object */
	control: Control<IFormValues>;
	/** React Hook Form state object */
	formState: FormState<IFormValues>;
	/** Whether fee calculation is in progress */
	isLoadingFees?: boolean;
	/** Whether checkout session is being created */
	isLoadingCheckoutSession?: boolean;
	/** Access token for file upload API calls */
	token: string;
}

/**
 * DonationForm Component
 *
 * Main public-facing donation form that donors use to make contributions.
 * Dynamically displays appropriate settings based on the selected donation frequency.
 *
 * Form Sections:
 * 1. Header with description and image
 * 2. Amount selection (frequency + amount picker)
 * 3. Specific cause selection (if enabled)
 * 4. Donor information collection
 * 5. Custom form fields (if configured)
 * 6. Submit button
 *
 * Frequency Handling:
 * - Automatically selects the correct settings (one-time, monthly, or annual) based on donor's frequency choice
 * - Falls back to one-time settings if the selected frequency is unavailable
 * - Fallback chain: selected frequency → one-time → root donation options
 *
 * @param survey - The donation survey configuration
 * @param formValues - Current form values
 * @param setValue - Function to update form field values
 * @param onSubmit - Form submission handler
 * @param control - React Hook Form control
 * @param formState - React Hook Form state
 * @param isLoadingFees - Whether fee calculation is in progress
 * @param isLoadingCheckoutSession - Whether checkout session is being created
 */
export const DonationForm = ({
	survey,
	formValues,
	setValue,
	onSubmit,
	control,
	formState,
	isLoadingFees,
	isLoadingCheckoutSession,
	token,
}: IProps) => {
	/**
	 * Gets the correct frequency settings based on the selected donation frequency.
	 * Implements a fallback chain to ensure settings are always available:
	 *
	 * Priority chain:
	 * 1. Selected frequency settings (monthly/annual)
	 * 2. One-time donation settings (if selected frequency unavailable)
	 * 3. Root donation options (legacy fallback)
	 *
	 * This ensures that if a frequency is selected but not configured,
	 * the form still displays valid settings instead of breaking.
	 */
	const currentFrequencySettings = useMemo(() => {
		const options = survey.donationOptions;

		// Priority 1: Use one-time settings when no frequency selected
		if (!formValues.donationFrequency) {
			return options.oneTimeDonationSettings || options;
		}

		// Priority 2: Use monthly settings with fallback to one-time
		if (formValues.donationFrequency === DonationFrequency.Monthly) {
			return (
				options.monthlyDonationSettings || // Try monthly first
				options.oneTimeDonationSettings || // Fallback to one-time
				options // Final fallback to root
			);
		}

		// Priority 3: Use annual settings with fallback to one-time
		if (formValues.donationFrequency === DonationFrequency.Yearly) {
			return (
				options.annualDonationSettings || // Try annual first
				options.oneTimeDonationSettings || // Fallback to one-time
				options // Final fallback to root
			);
		}

		// Default: Fallback to one-time settings
		return options.oneTimeDonationSettings || options;
	}, [survey.donationOptions, formValues.donationFrequency]);

	/**
	 * Handles frequency changes and resets the selected amount to the first option
	 * of the new frequency's suggested amounts.
	 *
	 * This ensures donors don't accidentally keep a selected amount that may not
	 * be available or appropriate for the new frequency.
	 */
	const handleFrequencyChange = useCallback(
		(frequency: DonationFrequency | null) => {
			setValue('donationFrequency', frequency);

			// Get the settings for the new frequency
			const options = survey.donationOptions;
			let newFrequencySettings;

			if (!frequency) {
				newFrequencySettings = options.oneTimeDonationSettings || options;
			} else if (frequency === DonationFrequency.Monthly) {
				newFrequencySettings = options.monthlyDonationSettings || options.oneTimeDonationSettings || options;
			} else if (frequency === DonationFrequency.Yearly) {
				newFrequencySettings = options.annualDonationSettings || options.oneTimeDonationSettings || options;
			} else {
				newFrequencySettings = options.oneTimeDonationSettings || options;
			}

			// Reset to the first suggested amount if available
			if (newFrequencySettings.enableSuggestedAmounts && newFrequencySettings.suggestedAmounts?.length > 0) {
				setValue('amount', newFrequencySettings.suggestedAmounts[0]);
				setValue('customAmount', null);
			}
		},
		[setValue, survey.donationOptions]
	);

	return (
		<form onSubmit={onSubmit}>
			<DonationSurveyHeader
				description={survey.donationOptions.donationDescription}
				image={survey.donationOptions.image?.url}
			/>

			<Separator />

			<DonationSurveyValuePicker
				survey={survey}
				allowCustomerToCoverServiceFees={survey.serviceFeeSettings?.allowCustomerToCoverServiceFees}
				allowCustomAmount={currentFrequencySettings.allowCustomAmount}
				customAmountMinimum={currentFrequencySettings.minimumAmount}
				enableSuggestedAmounts={currentFrequencySettings.enableSuggestedAmounts}
				customAmounts={
					currentFrequencySettings.suggestedAmounts?.length > 0 ? currentFrequencySettings.suggestedAmounts : []
				}
				allowMonthlyDonation={true}
				customerCoverFees={formValues.coverFees}
				selectedAmount={formValues.amount}
				customAmount={formValues.customAmount}
				donationFrequency={formValues.donationFrequency}
				onAmountChange={(amount: number) => setValue('amount', amount)}
				onCustomAmountChange={(customAmount: number) => setValue('customAmount', customAmount)}
				onCoverFeesChange={(coverFees: boolean) => setValue('coverFees', coverFees)}
				onFrequencyChange={handleFrequencyChange}
				formState={formState}
			/>

			<SpecificCauseSelector survey={survey} formValues={formValues} setValue={setValue} formState={formState} />

			<CustomerDataCollection
				firstName={formValues.firstName}
				lastName={formValues.lastName}
				donorName={formValues.donorName}
				email={formValues.email}
				phone={formValues.phone}
				address={formValues.address}
				setValue={setValue}
				collectDonorPhone={survey.donationOptions.collectDonorPhone}
				collectDonorAddress={survey.donationOptions.collectDonorAddress}
				allowAnonymousDonations={survey.donationOptions.allowAnonymousDonations}
				isAnonymous={formValues.isAnonymous}
				formState={formState}
			/>

			{!survey?.customFormIsDisabled && survey?.customForm?.fields?.length > 0 && control && formState && (
				<CustomForm survey={survey} control={control} formState={formState} setValue={setValue} token={token} />
			)}

			<Separator />

			<footer className={css(styleSheet.buttonContainer)}>
				<Button
					type='submit'
					styles={[styleSheet.buttonSubmit]}
					disabled={isLoadingFees || isLoadingCheckoutSession}
					isLoading={isLoadingFees || isLoadingCheckoutSession}
				>
					Save and Continue
				</Button>
			</footer>
		</form>
	);
};
